/**
 * Authentication Context - State management global pentru autentificare
 * Oferă acces la starea de autentificare și funcții pentru login/logout în întreaga aplicație
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import logger from '../utils/logger';
import { getCreditsStatus } from '../services/creditsService';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [credits, setCredits] = useState({
    available: 0,
    extra: 0,
    total: 0,
  });

  /**
   * Logout - Șterge token-ul și curăță starea
   */
  const logout = useCallback(() => {
    Cookies.remove('jwtToken');
    Cookies.remove('userId');
    Cookies.remove('subscriptionType');

    setUser(null);
    setCredits({
      available: 0,
      extra: 0,
      total: 0,
    });

    logger.log('User logged out');
  }, []);

  /**
   * Verifică și decodifică token-ul JWT
   */
  const checkAuth = useCallback(async () => {
    try {
      const token = Cookies.get('jwtToken');

      if (!token) {
        setUser(null);
        setLoading(false);
        return;
      }

      // Decodifică token-ul
      const decoded = jwtDecode(token);
      const exp = decoded.exp * 1000; // Convertim în milisecunde
      const currentTime = Date.now();

      // Verifică dacă token-ul a expirat
      if (exp < currentTime) {
        logger.warn('Token expirat - logout automat');
        logout();
        setLoading(false);
        return;
      }

      // Extrage informații din token
      const userName =
        decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'] ||
        'User';
      const userId = Cookies.get('userId');
      const subscriptionType = Cookies.get('subscriptionType');
      const subscriptionLevel = parseInt(decoded['SubscriptionLevel'] || '0', 10);
      const roleClaim = decoded['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] || decoded['role'] || [];
      const roles = Array.isArray(roleClaim) ? roleClaim : [roleClaim];

      setUser({
        name: userName,
        userId,
        subscriptionType,
        subscriptionLevel,
        roles,
        token,
      });

      // Încarcă creditele din backend
      await loadUserCredits();
    } catch (error) {
      logger.error('Eroare la verificarea autentificării:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [logout]);

  /**
   * Încarcă creditele utilizatorului din backend
   */
  const loadUserCredits = async () => {
    try {
      const response = await getCreditsStatus();

      if (response.success) {
        setCredits({
          available: response.availableCredits || 0,
          extra: response.extraCredits || 0,
          total: response.totalCredits || 0,
          pricePerCredit: response.pricePerCredit,
        });

        logger.log('Credits loaded:', {
          available: response.availableCredits,
          extra: response.extraCredits,
          total: response.totalCredits,
        });
      }
    } catch (error) {
      logger.error('Error loading credits:', error);
    }
  };

  /**
   * Login - Salvează token-ul și actualizează starea
   */
  const login = useCallback(async (token, userId, subscriptionType) => {
    try {
      // Calculează expirarea cookie-ului din JWT exp claim
      const decoded = jwtDecode(token);
      const expMs = decoded.exp * 1000;
      const cookieExpires = new Date(expMs);

      const cookieOptions = {
        expires: cookieExpires,
        secure: window.location.protocol === 'https:',
        sameSite: 'Strict',
      };

      // Salvează în cookies
      Cookies.set('jwtToken', token, cookieOptions);
      Cookies.set('userId', userId, cookieOptions);
      Cookies.set('subscriptionType', subscriptionType, cookieOptions);

      // Extrage informații utilizator din token-ul deja decodat
      const userName =
        decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'] ||
        'User';
      const subLevel = parseInt(decoded['SubscriptionLevel'] || '0', 10);
      const roleClaim = decoded['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] || decoded['role'] || [];
      const userRoles = Array.isArray(roleClaim) ? roleClaim : [roleClaim];

      setUser({
        name: userName,
        userId,
        subscriptionType,
        subscriptionLevel: subLevel,
        roles: userRoles,
        token,
      });

      // Încarcă creditele
      await loadUserCredits();

      logger.log('User logged in successfully:', userName);
    } catch (error) {
      logger.error('Error during login:', error);
      throw error;
    }
  }, []);
  /**
   * Refresh credits - Reîncarcă creditele utilizatorului
   */
  const refreshCredits = useCallback(async () => {
    await loadUserCredits();
  }, []);

  /**
   * Verifică autentificarea la montarea componentei
   */
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  /**
   * Event listener pentru actualizări credite
   */
  useEffect(() => {
    const handleCreditsUpdate = () => {
      logger.log('Credits update event received');
      refreshCredits();
    };

    window.addEventListener('creditsUpdated', handleCreditsUpdate);

    return () => {
      window.removeEventListener('creditsUpdated', handleCreditsUpdate);
    };
  }, [refreshCredits]);

  const isAdmin = user?.roles?.includes('Admin') || false;
  const isCRM = user?.roles?.includes('CRM') || false;
  const subscriptionLevel = user?.subscriptionLevel || 0;
  const hasActiveSubscription = subscriptionLevel >= 1 || isAdmin;

  const value = {
    user,
    loading,
    credits,
    isAuthenticated: !!user,
    isAdmin,
    isCRM,
    subscriptionLevel,
    hasActiveSubscription,
    login,
    logout,
    refreshCredits,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook pentru a accesa contextul de autentificare
 * @returns {Object} Context de autentificare
 * @throws {Error} Dacă este folosit în afara AuthProvider
 */
export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};

export default AuthContext;
